import { useEffect, useState } from 'react'
import { Table } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import { KTSVG } from '../../_metronic/helpers'
import { getRequestOptions } from '../helpers/Fetchwrapper'

interface InterBuLine {
	idSalesOrderRows: number
	productName: string
	packSize: string
	quantity: number
	unitName: string
	rate: number
	amount: number
	interBuRate: string
}

const numberFormat = (value: number) => {
	return value.toLocaleString('en-US', {
		style: 'decimal',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

function SalesInvoiceInterBu() {
	const { idSalesInvoice }: { idSalesInvoice: string } = useParams()
	const [lines, setLines] = useState<InterBuLine[]>([])
	const [invoiceNo, setInvoiceNo] = useState('')
	const [clientName, setClientName] = useState('')
	const [loading, setLoading] = useState(true)

	useEffect(() => {
		setLoading(true)
		fetch(process.env.REACT_APP_API_URL + 'sales-invoice/' + idSalesInvoice, getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const items = resp.data?.items || []
				const salesOrder = resp.data?.sales_order || resp.data?.salesOrder || {}
				setInvoiceNo(resp.data?.invoiceId || '')
				setClientName(salesOrder.company?.company_name || '')
				setLines(items.map((item: any) => {
					const orderRow = item.sales_order_row || item.salesOrderRow || {}
					const product = orderRow.product || {}
					const packUnit = product.item?.unit?.unit_name || ''

					return {
						idSalesOrderRows: orderRow.id_sales_order_rows || orderRow.idSalesOrderRows,
						productName: orderRow.alternate_name?.alternate_name
							|| orderRow.alternateName?.alternate_name
							|| product.product_name
							|| product.productName
							|| '',
						packSize: `${product.packing || ''} ${packUnit}`.trim(),
						quantity: parseFloat(item.delivered_quantity ?? item.deliveredQuantity ?? 0),
						unitName: product.unit?.unit_name || product.unit?.unitName || '',
						rate: parseFloat(orderRow.unit_rate ?? orderRow.unitRate ?? 0),
						amount: parseFloat(item.delivered_amount ?? item.deliveredAmount ?? 0),
						interBuRate: '',
					}
				}).filter((line: InterBuLine) => line.quantity > 0))
				setLoading(false)
			})
			.catch(() => {
				setLines([])
				setLoading(false)
			})
	}, [idSalesInvoice])

	const isValidRate = (line: InterBuLine) => {
		const rate = parseFloat(line.interBuRate)
		return line.interBuRate !== '' && !isNaN(rate) && rate >= 0 && rate <= line.rate
	}

	const interBuAmount = (line: InterBuLine) => {
		if (!isValidRate(line)) {
			return null
		}
		return Math.min(parseFloat((parseFloat(line.interBuRate) * line.quantity).toFixed(2)), line.amount)
	}

	const canPrint = lines.length > 0 && lines.every(isValidRate)

	const totalAmount = lines.reduce((sum, line) => sum + line.amount, 0)
	const interBuTotal = lines.reduce((sum, line) => sum + (interBuAmount(line) ?? 0), 0)

	const handleRateChange = (index: number, value: string) => {
		setLines((prev) => {
			const updated = [...prev]
			updated[index] = { ...updated[index], interBuRate: value }
			return updated
		})
	}

	const handlePrint = (part: 'entered' | 'remaining') => {
		if (!canPrint) {
			return
		}

		const params = new URLSearchParams()
		lines.forEach((line) => {
			params.append(`interBuRates[${line.idSalesOrderRows}]`, line.interBuRate)
		})
		params.append('interBuPart', part)
		window.open(
			`${process.env.REACT_APP_BACKEND}invoiceDownload/${idSalesInvoice}?${params.toString()}`,
			'_blank',
			'noopener,noreferrer'
		)
	}

	return (
		<div className='card card-custom'>
			<div className='card-header'>
				<div className="card-title">
					<KTSVG path='/media/svg/icons/Devices/Printer.svg' className='svg-icon-1 svg-icon-primary' />
					<span className='text fw-bolder' style={{ marginLeft: '10px' }}>
						Inter BU Invoice {invoiceNo && <>- {invoiceNo}</>}
					</span>
				</div>
				<div className="card-toolbar">
					<Link to='/sales-invoices' className="btn btn-sm btn-secondary">Back to Sales Invoices</Link>
				</div>
			</div>

			<div className='card-body'>
				{loading && <div>Loading...</div>}
				{!loading && (
					<>
						{clientName && <p className="mb-4"><b>Client:</b> {clientName}</p>}
						<p className="text-muted">
							Enter the Inter BU rate for each product. Invoice 1 prints with the rate you enter and Invoice 2 prints the total remaining amount as a single <b>Transportation and loading unloading cost</b> item. Nothing is saved.
						</p>
						<Table size="sm" bordered hover responsive>
							<thead>
								<tr>
									<th>SN</th>
									<th>Product</th>
									<th>Pack Size</th>
									<th className="text-end">Quantity</th>
									<th>Unit</th>
									<th className="text-end">Rate</th>
									<th className="text-end">Amount</th>
									<th className="text-end">Inter BU Rate</th>
									<th className="text-end">Invoice 1 Amount</th>
									<th className="text-end">Remaining Rate</th>
									<th className="text-end">Invoice 2 Amount</th>
								</tr>
							</thead>
							<tbody>
								{lines.map((line, index) => {
									const amount = interBuAmount(line)
									const hasError = line.interBuRate !== '' && !isValidRate(line)
									return (
										<tr key={line.idSalesOrderRows || index}>
											<td>{index + 1}</td>
											<td>{line.productName}</td>
											<td>{line.packSize}</td>
											<td className="text-end">{line.quantity}</td>
											<td>{line.unitName}</td>
											<td className="text-end">{numberFormat(line.rate)}</td>
											<td className="text-end">{numberFormat(line.amount)}</td>
											<td style={{ minWidth: '140px' }}>
												<input
													type="number"
													min={0}
													max={line.rate}
													step="0.01"
													className={`form-control form-control-sm text-end ${hasError ? 'is-invalid' : ''}`}
													value={line.interBuRate}
													onChange={(event) => handleRateChange(index, event.target.value)}
												/>
												{hasError && <div className="invalid-feedback">Must be between 0 and {numberFormat(line.rate)}</div>}
											</td>
											<td className="text-end">{amount === null ? '' : numberFormat(amount)}</td>
											<td className="text-end">{amount === null ? '' : numberFormat(line.rate - parseFloat(line.interBuRate))}</td>
											<td className="text-end">{amount === null ? '' : numberFormat(line.amount - amount)}</td>
										</tr>
									)
								})}
								{lines.length === 0 && (
									<tr>
										<td colSpan={11} className="text-center text-muted">No products found on this invoice.</td>
									</tr>
								)}
							</tbody>
							{lines.length > 0 && (
								<tfoot>
									<tr>
										<th colSpan={6} className="text-end">Total</th>
										<th className="text-end">{numberFormat(totalAmount)}</th>
										<th></th>
										<th className="text-end">{numberFormat(interBuTotal)}</th>
										<th></th>
										<th className="text-end">{canPrint ? numberFormat(totalAmount - interBuTotal) : ''}</th>
									</tr>
								</tfoot>
							)}
						</Table>

						<div className="d-flex justify-content-end mt-4">
							<button type="button" className="btn btn-sm btn-primary me-2" onClick={() => handlePrint('entered')} disabled={!canPrint}>
								Print Invoice 1 (Inter BU Rate)
							</button>
							<button type="button" className="btn btn-sm btn-primary" onClick={() => handlePrint('remaining')} disabled={!canPrint}>
								Print Invoice 2 (Remaining Rate)
							</button>
						</div>
					</>
				)}
			</div>
		</div>
	)
}

export default SalesInvoiceInterBu
